import { useState, useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import { Sparkles, Edit, AlertCircle, Gift } from "lucide-react";
import CardPhoto from "../components/quiz/stepper/CardPhoto";
import {
  BookingProvider,
  useBooking,
  NoShowDisclaimer,
  EventSelector,
  CitySelector,
  NeighborhoodSelector,
  BookingDetailsSelector,
  LanguageSelector,
  BookingFlowModals,
} from "../components/booking";
import { useSearchCities, useProfile } from "@/lib/api";
import { useT } from "@/lib/i18n";
import tracking from "@/lib/tracking";
import { formatDate, DateFormats, todayjs } from "@/utils/dateUtils";
import dayjs from "dayjs";
import { BOOKING_CUTOFF_HOURS, CANCELLATION_REFUND_WINDOW_HOURS } from "@shared/constants";
import { Loader } from '@/components/Loader';

function BookingContent() {
  const { t } = useT();
  useEffect(() => {
    tracking.trackFlow('booking');
    tracking.bookingScreenViewed('control');
  }, []);
  const {
    selectedCity,
    selectedNeighborhood,
    selectedEventId,
    neighborhoods,
    allEvents,
    selectedEvent,
    isLoading,
    neighborhoodsLoading,
    specialRequests,
    isEditMode,
    existingBooking,
    profile,
    setSelectedCity,
    setSelectedNeighborhood,
    setSelectedEventId,
    setSpecialRequests,
    handleCreateReservation,
    isCreatingReservation,
  } = useBooking();

  const [validationError, setValidationError] = useState<string | null>(null);

  const { data: profileData } = useProfile();
  const isSubscriber = useMemo(
    () =>
      (profileData?.data?.entitlements || []).some(
        (e) => e.type === "subscription" && e.status === "active",
      ),
    [profileData?.data?.entitlements],
  );

  // Get selected city data to access localLanguage for preferences
  const { data: citiesData } = useSearchCities("", selectedCity);
  const selectedCityData = useMemo(
    () => citiesData?.data?.find((c) => c.id === selectedCity),
    [citiesData?.data, selectedCity]
  );

  const languageRequired = !!selectedCityData?.localLanguage;
  const languageFilled = !languageRequired || (specialRequests?.language && specialRequests.language.length > 0);
  const isFormComplete = selectedCity && selectedNeighborhood && selectedEventId && languageFilled;

  const selectedNeighborhoodFull = useMemo(
    () => neighborhoods.find((n: any) => n.id === selectedNeighborhood?.id),
    [neighborhoods, selectedNeighborhood?.id]
  );
  const isBookingFree =
    !!selectedEvent?.date &&
    !!selectedNeighborhoodFull?.freeUntil &&
    selectedEvent.date <= selectedNeighborhoodFull.freeUntil;

  const isLastMinuteBooking = useMemo(() => {
    if (!selectedEvent?.date || !selectedEvent?.time) return false;
    const hoursUntilEvent = todayjs(selectedEvent.date, selectedEvent.time).diff(dayjs(), "hour");
    return (
      hoursUntilEvent >= BOOKING_CUTOFF_HOURS &&
      hoursUntilEvent < CANCELLATION_REFUND_WINDOW_HOURS
    );
  }, [selectedEvent?.date, selectedEvent?.time]);

  // Clear validation error when selections change
  useEffect(() => {
    setValidationError(null);
  }, [selectedCity, selectedNeighborhood, selectedEventId, specialRequests?.language]);

  // Track booking step selections
  const handleCitySelect = (cityId: string) => {
    setSelectedCity(cityId);
    const city = citiesData?.data?.find((c) => c.id === cityId);
    tracking.bookingCitySelected(cityId, city?.name || cityId);
  };

  const handleEventSelect = (eventId: string) => {
    setSelectedEventId(eventId);
    tracking.bookingEventSelected(eventId);
  };

  const handleNeighborhoodSelect = (neighborhood: any) => {
    setSelectedNeighborhood(neighborhood);
    tracking.bookingNeighborhoodSelected(neighborhood?.id || '', neighborhood?.name || '');
  };

  const handleContinueClick = () => {
    if (!selectedCity) {
      setValidationError(t('booking.validation.selectCity'));
      return;
    }
    if (!selectedEventId) {
      setValidationError(t('booking.validation.selectEvent'));
      return;
    }
    if (!selectedNeighborhood) {
      setValidationError(t('booking.validation.selectNeighborhood'));
      return;
    }
    if (selectedCityData?.localLanguage && (!specialRequests?.language || specialRequests.language.length === 0)) {
      setValidationError(t('booking.validation.selectLanguage'));
      return;
    }
    setValidationError(null);
    tracking.bookingContinueClicked('control');
    handleCreateReservation();
  };

  // Wait until profile location was loaded.
  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-48">
        <Loader />
      </div>
    );
  }

  return (
    <div className="pt-4 pb-24 md:pt-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <h1 className="text-4xl md:text-5xl font-bold mb-2 flex items-center gap-2">
            {isEditMode ? (
              <>
                <Edit className="w-8 h-8 text-primary-500" />
                {t('booking.edit.title')}
              </>
            ) : (
              t('booking.title')
            )}
          </h1>
          {isEditMode && existingBooking && (
            <p className="text-dark-muted text-lg">
              {t('booking.edit.subtitle')}
            </p>
          )}
        </motion.div>

        {/* Location Selection */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="card overflow-hidden"
        >
          <div className="-mx-6 -mt-6 mb-6 aspect-[3/1] overflow-hidden max-h-48">
            <img
              src={'/img/booking_screen.webp'}
              alt=""
              className="w-full h-full object-cover"
            />
          </div>
          <CitySelector
            selectedCity={selectedCity}
            onCitySelect={handleCitySelect}
            initiallyCollapsed={true}
          />

          {selectedCity && (
            <EventSelector
              events={allEvents}
              selectedEventId={selectedEventId}
              onEventSelect={handleEventSelect}
              initiallyCollapsed={!!selectedEventId}
            />
          )}

          {selectedCity && selectedEventId && (
            <NeighborhoodSelector
              neighborhoods={neighborhoods}
              selectedNeighborhood={selectedNeighborhood}
              onNeighborhoodSelect={handleNeighborhoodSelect}
              isLoading={neighborhoodsLoading}
            />
          )}

          {selectedCityData?.localLanguage && (
            <LanguageSelector
              localLanguage={selectedCityData.localLanguage}
              selectedLanguages={specialRequests?.language}
              onLanguageSelect={(languages) => {
                const updated = { ...specialRequests };
                if (languages === undefined) {
                  delete updated.language;
                } else {
                  updated.language = languages;
                }
                setSpecialRequests(updated);
              }}
            />
          )}

          <BookingDetailsSelector
            value={specialRequests}
            initiallyOpened={selectedNeighborhood ? true : false}
            onChange={setSpecialRequests}
            profile={profile}
          />
        </motion.div>

        {isBookingFree && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-4 flex items-center gap-2 rounded-3xl bg-primary-500 text-white px-4 py-3 text-sm font-semibold shadow-md"
            >
              <Gift className="w-4 h-4 flex-shrink-0" />
              <span>{t('booking.freeBanner', {
                neighborhood: selectedNeighborhoodFull?.name,
                date: selectedNeighborhoodFull?.freeUntil ? formatDate(
                  (() => {
                    const [y, m, d] = selectedNeighborhoodFull.freeUntil.split('-').map(Number);
                    return new Date(y, m - 1, d);
                  })(),
                  DateFormats.monthDay,
                ) : '',
              })}</span>
            </motion.div>
          )}
        {isLastMinuteBooking && (
          <div className="mt-4">
            <CardPhoto
              photoSrc="/img/sections/last_minute.webp"
              primary="#F59E0B"
              glow="rgba(245, 158, 11, 0.25)"
              isSelected={false}
            >
              <div className="font-Poppins text-[18px] leading-tight font-semibold text-neutral-900 dark:text-neutral-50">
                {t('booking.lastMinute.title')}
              </div>
              <div className="text-[12px] leading-snug mt-1.5">
                {t('booking.lastMinute.notice')}
                {!isSubscriber && '*'}
              </div>
            </CardPhoto>
          </div>
        )}
        <NoShowDisclaimer />
        {/* Submit Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-6"
        >
          <button
            onClick={handleContinueClick}
            disabled={isCreatingReservation}
            className={`btn w-full py-8 text-lg ${
              isFormComplete ? "btn-primary" : "btn-primary opacity-50"
            }`}
          >
            {isCreatingReservation ? (
              <span className="flex items-center justify-center">
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                {isEditMode ? t('booking.updatingReservation') : t('booking.creatingReservation')}
              </span>
            ) : (
              <span className="flex items-center justify-center">
                {isEditMode ? (
                  <>
                    <Edit className="w-5 h-5 mr-2" />
                    {t('booking.updateReservation')}
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5 mr-2" />
                    {t('common.buttons.next')}
                  </>
                )}
              </span>
            )}
          </button>
          {validationError && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-3 flex items-center gap-2 text-white text-sm"
            >
              <AlertCircle className="w-4 h-4" />
              {validationError}
            </motion.div>
          )}
          {isLastMinuteBooking && !isSubscriber && (
            <p className="mt-3 text-xs">
              * {t('booking.lastMinute.singleTicketNotice')}
            </p>
          )}
        </motion.div>
      </div>

      <BookingFlowModals />
    </div>
  );
}

export default function Booking() {
  return (
    <BookingProvider>
      <BookingContent />
    </BookingProvider>
  );
}
